<template>
  <div class="console-page dashboard-view">
    <a-spin :spinning="confirmLoading" tip="Loading dashboard…">
      <h1 class="console-page__title">Dashboard</h1>
      <p class="console-page__subtitle">
        Overview of registered users and recent activity
      </p>

      <a-row :gutter="[24, 24]">
        <a-col :xs="24" :sm="12" :lg="8">
          <a-card :bordered="false" class="dashboard-view__hero-stat">
            <a-statistic title="Total users" :value="totalUser">
              <template #prefix>
                <a-icon type="team" class="dashboard-view__hero-icon" />
              </template>
            </a-statistic>
          </a-card>
        </a-col>
      </a-row>

      <h2 class="console-page__section-title">User information</h2>

      <a-row v-if="periodSections.length" :gutter="[24, 24]">
        <a-col
          v-for="[sectionName, item] in periodSections"
          :key="sectionName"
          :xs="24"
          :sm="12"
          :lg="6"
        >
          <a-card
            :bordered="false"
            class="dashboard-view__period-card"
            :title="sectionName"
          >
            <div class="dashboard-view__metric">
              <span class="dashboard-view__metric-label">
                <a-icon type="user-add" />
                New users
              </span>
              <span class="dashboard-view__metric-value">{{
                newUserTotal(item)
              }}</span>
            </div>
            <div
              v-if="
                sectionName === 'Today' || sectionName === 'Previous Day'
              "
              class="dashboard-view__metric"
            >
              <span class="dashboard-view__metric-label">
                <a-icon type="unlock" />
                Active users
              </span>
              <span class="dashboard-view__metric-value">{{
                item.active_user
              }}</span>
            </div>
          </a-card>
        </a-col>
      </a-row>

      <a-empty
        v-else-if="!confirmLoading"
        description="No user metrics available"
        class="dashboard-view__empty"
      />
    </a-spin>
  </div>
</template>

<script>
import { sendRequest } from "@/http/axios.method";

const PERIOD_ORDER = [
  "Today",
  "Previous Day",
  "This Month",
  "Last Month",
];

export default {
  data() {
    return {
      config: {
        Authorization: "Bearer " + this.$store.state.authUser.token,
      },
      dashboardReports: [],
      userInfo: {
        Today: {
          social_user: 0,
          without_social_user: 0,
          guest_user: 0,
          active_user: 0,
        },
        "Previous Day": {
          social_user: 0,
          without_social_user: 0,
          guest_user: 0,
          active_user: 0,
        },
        "This Month": {
          social_user: 0,
          without_social_user: 0,
          guest_user: 0,
          active_user: 0,
        },
        "Last Month": {
          social_user: 0,
          without_social_user: 0,
          guest_user: 0,
          active_user: 0,
        },
      },
      totalUser: 0,
      confirmLoading: false,
    };
  },
  computed: {
    periodSections() {
      const info = this.userInfo;
      if (!info || typeof info !== "object") return [];

      const entries = Object.entries(info).filter(
        ([key]) => key !== "totalUser"
      );

      entries.sort((a, b) => {
        const ia = PERIOD_ORDER.indexOf(a[0]);
        const ib = PERIOD_ORDER.indexOf(b[0]);
        const sa = ia === -1 ? PERIOD_ORDER.length : ia;
        const sb = ib === -1 ? PERIOD_ORDER.length : ib;
        return sa - sb;
      });

      return entries;
    },
  },
  mounted() {
    this.dashboardReport();
  },
  methods: {
    newUserTotal(item) {
      const social = Number(item?.social_user) || 0;
      const system = Number(item?.without_social_user) || 0;
      const guest = Number(item?.guest_user) || 0;
      return social + system + guest;
    },
    dashboardReport() {
      this.confirmLoading = true;
      sendRequest("get", "admin/get-dashbaord-data", {}, this.config)
        .then((response) => {
          this.confirmLoading = false;
          if (response.success) {
            const responses = response.data;
            this.totalUser = responses.totalUser;
            this.userInfo = responses;
          } else {
            if (response.status_code === 401) {
              this.$store.dispatch("logout");
              this.$router.push("/login");
            }
            if (response.status_code === 500) {
              this.revenueReportData = [];
            }
          }
        })
        .catch(() => {
          this.confirmLoading = false;
        })
        .finally(() => {
          this.confirmLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/base/variables";

.dashboard-view__hero-stat {
  border-radius: 12px !important;
  box-shadow: $shadow-2 !important;
  border: none !important;
  background: linear-gradient(135deg, $color-primary 0%, #028a62 100%) !important;

  ::v-deep .ant-card-body {
    padding: 28px 24px;
  }

  ::v-deep .ant-statistic-title {
    color: rgba(255, 255, 255, 0.88);
    font-size: 14px;
    font-weight: $fw-semibold;
    margin-bottom: 8px;
  }

  ::v-deep .ant-statistic-content {
    color: $color-white;
    font-size: 28px;
    font-weight: $fw-bold;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  ::v-deep .ant-statistic-content-value {
    font-variant-numeric: tabular-nums;
  }
}

.dashboard-view__hero-icon {
  font-size: 26px;
  opacity: 0.95;
}

.dashboard-view__period-card {
  border-radius: 12px !important;
  box-shadow: $shadow-2 !important;
  border: none !important;
  height: 100%;

  ::v-deep .ant-card-head {
    border-bottom: 1px solid $color-gray-4;
    min-height: 52px;
    padding: 0 20px;
  }

  ::v-deep .ant-card-head-title {
    padding: 14px 0;
    font-weight: $fw-semibold;
    font-size: 15px;
    color: $color-gray-10;
  }

  ::v-deep .ant-card-body {
    padding: 16px 20px 20px;
  }
}

.dashboard-view__metric {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid $color-gray-3;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
}

.dashboard-view__metric-label {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: $fw-semibold;
  color: $color-gray-8;

  .anticon {
    color: $color-primary;
    font-size: 16px;
  }
}

.dashboard-view__metric-value {
  font-size: 22px;
  font-weight: $fw-bold;
  color: $color-gray-10;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

.dashboard-view__empty {
  margin: 48px auto;
}
</style>
